<template>
  <CBox>
    <Header :showLogo="true"></Header>
    <omint-page-content class="verification-content">
      <OmtTypography mb="8px" as="h1-bold"> Verificação em duas etapas </OmtTypography>
      <OmtTypography mb="24px" as="h3-semi-bold"> Insira o código que enviamos para: </OmtTypography>
      <CBox bg="#F6F6F9" p="16px" mb="24px">
        <CFlex mb="16px" alignItems="center">
          <OmtIcons mr="16px" size="lg" name="address-card" />
          <OmtTypography as="h2-bold">Contatos</OmtTypography></CFlex
        >
        <CBox v-if="this.mfa_email" mb="8px">
          <OmtTypography color="var(--colors-light-primary-gray2)" as="p2">E-mail </OmtTypography>
          <OmtTypography as="p1"> {{ this.mfa_email }} </OmtTypography></CBox
        >
        <CBox v-if="this.mfa_telefone">
          <OmtTypography color="var(--colors-light-primary-gray2)" as="p2">
            SMS para o celular com o final</OmtTypography
          >
          <OmtTypography as="p1"> {{ this.mfa_telefone }} </OmtTypography></CBox
        >
        <CDivider borderWidth="0.5px"></CDivider>
        <OmtTypography mb="16px" color="var(--colors-light-primary-gray2)" as="p2">
          Caso o código não chegue em até 10 minutos verifique sua caixa de spam ou clique abaixo:
        </OmtTypography>
        <CFlex justifyContent="center">
          <OmtButton
            @click="login(true)"
            color="var(--colors-light-primary-navy-blue)"
            variant="ghost"
            p="0"
            height="auto"
            fontSize="14px"
            fontWeight="700"
          >
            Reenviar código
          </OmtButton></CFlex
        >
      </CBox>

      <CBox pb="24px">
        <OmtTypography color="var(--colors-light-primary-gray2)" as="p2"> Código de confirmação </OmtTypography>
        <OmtInput
          v-mask="'####'"
          v-model="mfa_code"
          :height="27"
          fontSize="14px"
          fontWeight="700"
          type="number"
          @focus="inputFocus = true"
          @blur="inputFocus = false"
          :class="{ 'input-fixed': (iOS || isAndroid) && inputFocus }"
          :style="(iOS || isAndroid) && inputFocus ? inputFixedStyle : null"
        />
      </CBox>

      <FixedButtonRow :useFooter="false">
        <OmtButton @click="login(false)" :w="['100%', '308px !important']" height="58px" mt="16px"> Validar </OmtButton>
      </FixedButtonRow>
    </omint-page-content>
  </CBox>
</template>

<script>
import FixedButtonRow from "@/components/Atoms/FixedButtonRow/index.vue";
import Header from "@/components/Molecules/Header/index.vue";
import { ROTA_BASE, ROTA_PADRAO, ROTA_VINCULOS, ROTA_OPTIN } from "@/utils/perfilRouteUtil";
import { mapActions, mapMutations, mapState } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

export default {
  components: {
    FixedButtonRow,
    Header
  },
  data() {
    return {
      mfa_code: "",
      msgErro: "",
      inputFocus: false
    };
  },
  computed: {
    ...mapState("biometrics", ["shoudUseBiometric"]),
    ...mapState("saude", [
      "usernameMFA",
      "passwordMFA",
      "mfa_key",
      "mfa_email",
      "mfa_telefone",
      "typeLogin",
      "fallbackUsernameMFA",
      "fallbackPasswordMFA",
      "fallbackTypeLoginMFA"
    ]),
    hasValidNextUrl() {
      return (
        this.$route.query.nextUrl &&
        this.$route.query.nextUrl !== ROTA_BASE &&
        this.$route.query.nextUrl !== ROTA_PADRAO &&
        this.$route.query.nextUrl !== ROTA_OPTIN &&
        this.$route.query.nextUrl !== ROTA_VINCULOS
      );
    },
    username() {
      return this.usernameMFA ?? this.fallbackUsernameMFA ?? this.typeLogin ?? this.fallbackTypeLoginMFA;
    },
    password() {
      return this.passwordMFA ?? this.fallbackPasswordMFA;
    },
    iOS() {
      return navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    isAndroid() {
      return navigator.userAgent.match(/Android/i);
    },
    inputFixedStyle() {
      return {
        position: "fixed",
        left: "16px",
        right: "16px",
        width: "100%",
        bottom: "calc(env(safe-area-inset-bottom) + 74px)",
        zIndex: 2
      };
    }
  },
  mounted() {
    if (!this.mfa_key) {
      this.$router.replace(ROTA_BASE);
    }
  },
  methods: {
    ...mapActions("saude", ["loginUser"]),
    ...mapMutations("saude", ["SET_MFA_INFOS", "CLEAR_MFA_INFOS"]),
    ...mapActions("biometrics", [
      "authenticateUser",
      "registerListeners",
      "setAuthenticated",
      "setUserAuthenticationState"
    ]),
    ...mapMutations("saude", ["LOGOUT_USER"]),
    async login(resetCode) {
      let ok = false;
      let nextRoute;
      if (!navigator.onLine) {
        window.dispatchEvent(new Event("offline"));
        return;
      }
      try {
        this.$root.$emit("show-loading");
        const username = this.username;
        const password = this.password;

        nextRoute = await this.loginUser({
          username: username,
          password: password,
          mfa_code: resetCode ? "" : this.mfa_code,
          mfa_key: resetCode ? "" : this.mfa_key
        });

        ok = nextRoute !== "" && nextRoute !== ROTA_PADRAO;

        // Faz o redirect para a rota de origem (nextUrl) logo após a validação de MFA
        if (this.$route.query.nextUrl) {
          setTimeout(() => {
            this.$router.replace(this.$route.query.nextUrl);
          }, 1000);
        }

        this.CLEAR_MFA_INFOS();
      } catch (error) {
        const msg = error.message;
        this.msgErro = msg;

        if (!resetCode) {
          Swal.fire({
            icon: "error",
            text: "O código informado está incorreto. Por favor, verifique os dados e tente novamente."
          });
        }
        this.getInfosMFA(error.response?.headers);
      } finally {
        this.$root.$emit("hide-loading");
      }

      if (ok) {
        this.setUserAuthenticationState(true);
        if (this.shoudUseBiometric) {
          this.setAuthenticated();
        }
        try {
          if (nextRoute.startsWith("/")) {
            if (this.hasValidNextUrl) {
              if (nextRoute === ROTA_VINCULOS || nextRoute === ROTA_OPTIN) {
                await this.$router.replace({
                  path: nextRoute,
                  query: {
                    nextUrl: this.$route.query.nextUrl
                  }
                });
              } else {
                await this.$router.replace(this.$route.query.nextUrl);
              }
            } else {
              await this.$router.replace(nextRoute);
            }
          } else {
            await this.LOGOUT_USER();
            window.location = nextRoute;
          }
        } catch (error) {
          const msg = error.message;

          // Se o erro for de redirect cancelado, faz o redirect para a rota de origem (nextUrl)
          if (msg.includes("Redirected when going from") || msg.includes("Navigation cancelled")) {
            this.$router.replace(this.$route.query.nextUrl);
          } else {
            console.error("Erro no redirect:", error);
          }
        }
      }
    },
    getInfosMFA(resp) {
      const celular = this.mfa_telefone;
      const email = this.mfa_email;
      const username = this.username;
      const password = this.password;

      this.SET_MFA_INFOS({
        mfa_key: resp["x-mfa-key"],
        mfa_email: email,
        username: username,
        password: password,
        telefone: celular
      });
    }
  }
};
</script>

<style scoped>
.verification-content {
  min-height: calc(100dvh - 120px);
  padding-bottom: calc(84px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.input-fixed {
  background: #fff;
}

@supports not (padding-bottom: env(safe-area-inset-bottom)) {
  .verification-content {
    padding-bottom: 84px;
  }
}
</style>
